import is from '@sindresorhus/is';
import type z from 'zod';

import type { JobProfileComponent, jobProfileStructuredDescriptionSchema } from '~/shared/data-contract/job-profile';
import type { JobProfileSkill } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import {
    addSkillsToCompetencies,
    type JobProfileSkillWithDiff,
    type SkillDiff,
} from '../job-profile-section/skills/use-skills-with-compentencies';
import type { CompetencyWithSkills } from '../job-profile-section/types';

type StructuredDescription = z.infer<typeof jobProfileStructuredDescriptionSchema>;

const getCompetenciesWithSkills = (profile: JobProfileComponent | undefined): readonly CompetencyWithSkills[] =>
    addSkillsToCompetencies([...(profile?.competencies?.values() ?? [])], profile?.skills ?? []).filter((competency) =>
        is.nonEmptyArray(competency.skills),
    );

const findMatchingSection = (
    sections: readonly StructuredDescription[] | undefined,
    id: string | undefined,
    tag: string,
): StructuredDescription | undefined => {
    if (is.undefined(sections)) return undefined;

    const idToMatch = id ?? tag;
    return sections.find((s) => (s.id ?? s.tag) === idToMatch) ?? sections.find((s) => s.tag === tag);
};

const skillDiff = (
    skill: JobProfileSkill,
    compareWith: JobProfileComponent | undefined,
    type: 'added' | 'removed',
    compareLevels: boolean,
): SkillDiff | undefined => {
    const counterpart = compareWith?.skills?.find((s) => s.id === skill.id);
    if (is.undefined(counterpart)) return type;
    return compareLevels && (skill.level ?? null) !== (counterpart.level ?? null) ? 'changed' : undefined;
};

export const useCompetenciesWithSkillsAndDiff = (
    details: JobProfileComponent | undefined,
    compareWith: JobProfileComponent | undefined,
    type: 'added' | 'removed',
    { compareLevels = false }: { readonly compareLevels?: boolean } = {},
): readonly (Omit<CompetencyWithSkills, 'skills'> & { skills: JobProfileSkillWithDiff[] })[] => {
    const competenciesWithSkills = getCompetenciesWithSkills(details);
    const comparedCompetencyIds = new Set(getCompetenciesWithSkills(compareWith).map((competency) => competency.id));

    return competenciesWithSkills.map((competency) => {
        return {
            ...competency,
            diff: comparedCompetencyIds.has(competency.id) ? undefined : type,

            skills: competency.skills.map((skill) => {
                return {
                    ...skill,
                    diff: skillDiff(skill, compareWith, type, compareLevels),
                };
            }),
        };
    });
};

const stripOuterPTag = (html: string): string => {
    return html.replace(/^<p>/, '').replace(/<\/p>$/, '');
};

export const matchSections = (
    old: JobProfileComponent,
    current: JobProfileComponent,
    activeLanguage: LanguageCodeForJSA,
) => {
    const currentStructured = current.translations.find((t) => t.language === activeLanguage)?.structured;
    const oldStructured = old.translations.find((t) => t.language === activeLanguage)?.structured;

    return (
        currentStructured?.map((section) => ({
            current: { ...section, value: stripOuterPTag(section.value) },
            old: findMatchingSection(oldStructured, section.id, section.tag),
        })) ?? []
    );
};
